import React, { useEffect, useState, useCallback } from 'react';
import { api, type ProfileDetail } from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmtDate, fmtClock } from '../lib/format';
import { OperaWindow, OperaSection, OperaField, type SideButton, type ActionStripTab } from './OperaWindow';
import { ProfileNotesTab } from './ProfileNotesTab';

interface Props {
  profileId: string;
  onClose: () => void;
  onChanged: () => void;
}

type Mode = 'view' | 'edit' | 'archive-confirm' | 'unarchive-confirm' | 'add-alias' | 'grant-portal-confirm' | 'portal-result';

/** Which top-level view of the record is showing. */
type Panel = 'details' | 'notes';

function statusLabel(s: string): string {
  switch (s) {
    case 'TENTATIVE':   return 'Tentative';
    case 'CONFIRMED':   return 'Confirmed';
    case 'CHECKED_IN':  return 'In-house';
    case 'CHECKED_OUT': return 'Checked out';
    case 'NO_SHOW':     return 'No show';
    case 'CANCELLED':   return 'Cancelled';
    default:            return s;
  }
}

function dateOnlyForInput(iso: string | null): string {
  if (!iso) return '';
  return iso.substring(0, 10);
}

export function ProfileDetailPanel({ profileId, onClose, onChanged }: Props) {
  const { user } = useAuth();
  const canArchive = user?.role === 'ADMIN' || user?.role === 'MANAGER';
  const canGrantPortal = user?.role === 'ADMIN' || user?.role === 'MANAGER';

  const [data, setData] = useState<ProfileDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('view');
  const [panel, setPanel] = useState<Panel>('details');
  const [busy, setBusy] = useState(false);

  // Edit form state
  const [workingName, setWorkingName] = useState('');
  const [phone,       setPhone]       = useState('');
  const [email,       setEmail]       = useState('');
  const [dob,         setDob]         = useState('');
  const [notes,       setNotes]       = useState('');

  // Archive
  const [archiveReason, setArchiveReason] = useState('');
  const [unarchiveReason, setUnarchiveReason] = useState('');

  // Portal access (grant or reset)
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [isReset, setIsReset] = useState(false);
  const [copiedField, setCopiedField] = useState<'password' | 'username' | null>(null);

  // Add alias
  const [newAlias,      setNewAlias]      = useState('');
  const [newAliasNotes, setNewAliasNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.profileDetail(profileId);
      setData(d);
      setWorkingName(d.workingName);
      setPhone(d.phone ?? '');
      setEmail(d.email ?? '');
      setDob(dateOnlyForInput(d.dateOfBirth));
      setNotes(d.notes ?? '');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => { load(); }, [load]);

  async function handleSave() {
    if (!workingName.trim()) { setError('Working name cannot be empty.'); return; }
    setBusy(true);
    setError(null);
    try {
      await api.updateProfile(profileId, {
        workingName: workingName.trim(),
        phone: phone.trim() || null,
        email: email.trim() || null,
        dateOfBirth: dob || null,
        notes: notes.trim() || null,
      });
      setMode('view');
      await load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleArchive() {
    if (!archiveReason.trim()) { setError('Reason is required.'); return; }
    setBusy(true);
    setError(null);
    try {
      await api.archiveProfile(profileId, archiveReason.trim());
      onChanged();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  async function handleUnarchive() {
    if (!unarchiveReason.trim()) { setError('Reason is required.'); return; }
    setBusy(true);
    setError(null);
    try {
      await api.unarchiveProfile(profileId, unarchiveReason.trim());
      setUnarchiveReason('');
      setMode('view');
      await load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleGrantPortalAccess() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.grantPortalAccess(profileId);
      setTempPassword(res.tempPassword);
      setIsReset(res.isReset);
      setMode('portal-result');
      // Reload profile to reflect new portal state
      await load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setMode('view');
    } finally {
      setBusy(false);
    }
  }

  function copyToClipboard(text: string, which: 'password' | 'username') {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        setCopiedField(which);
        setTimeout(() => setCopiedField(null), 2000);
      }).catch(() => {
        setError('Could not copy to clipboard.');
      });
    }
  }

  async function handleAddAlias() {
    if (!newAlias.trim()) { setError('Alias cannot be empty.'); return; }
    setBusy(true);
    setError(null);
    try {
      await api.addAlias(profileId, newAlias.trim(), newAliasNotes.trim());
      setNewAlias('');
      setNewAliasNotes('');
      setMode('view');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleRemoveAlias(aliasId: string) {
    setBusy(true);
    setError(null);
    try {
      await api.deleteAlias(profileId, aliasId);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (loading || !data) {
    return (
      <OperaWindow title="INDIVIDUAL" onClose={onClose} size="large"
        sideButtons={[{ label: 'Close', onClick: onClose }]}>
        <div className="loading">{error ?? 'Loading…'}</div>
      </OperaWindow>
    );
  }

  // Side button stack varies by mode
  let sideButtons: SideButton[];
  if (mode === 'edit') {
    sideButtons = [
      { label: 'Save',   onClick: handleSave, primary: true, disabled: busy },
      { label: 'Cancel', onClick: () => { setMode('view'); load(); }, disabled: busy },
    ];
  } else if (mode === 'archive-confirm') {
    sideButtons = [
      { label: 'Confirm', onClick: handleArchive, danger: true, disabled: busy },
      { label: 'Back',    onClick: () => { setMode('view'); setArchiveReason(''); }, disabled: busy },
    ];
  } else if (mode === 'unarchive-confirm') {
    sideButtons = [
      { label: 'Restore', onClick: handleUnarchive, primary: true, disabled: busy },
      { label: 'Back',    onClick: () => { setMode('view'); setUnarchiveReason(''); }, disabled: busy },
    ];
  } else if (mode === 'add-alias') {
    sideButtons = [
      { label: 'Add',     onClick: handleAddAlias, primary: true, disabled: busy },
      { label: 'Cancel',  onClick: () => { setMode('view'); setNewAlias(''); setNewAliasNotes(''); }, disabled: busy },
    ];
  } else if (mode === 'grant-portal-confirm') {
    sideButtons = [
      { label: data.hasPortalAccess ? 'Reset Password' : 'Grant Access', onClick: handleGrantPortalAccess, primary: true, disabled: busy },
      { label: 'Cancel', onClick: () => setMode('view'), disabled: busy },
    ];
  } else if (mode === 'portal-result') {
    sideButtons = [
      { label: 'Done', onClick: () => { setMode('view'); setTempPassword(null); setCopiedField(null); }, primary: true },
    ];
  } else {
    sideButtons = [
      !data.isArchived ? { label: 'Edit',    onClick: () => setMode('edit') } : null,
      !data.isArchived ? { label: 'Alias',   onClick: () => setMode('add-alias') } : null,
      (!data.isArchived && data.isActive && canGrantPortal) ? { label: data.hasPortalAccess ? 'Reset Portal' : 'Grant Portal', onClick: () => setMode('grant-portal-confirm') } : null,
      (!data.isArchived && canArchive) ? { label: 'Archive', onClick: () => setMode('archive-confirm'), danger: true } : null,
      (data.isArchived && canArchive) ? { label: 'Restore', onClick: () => setMode('unarchive-confirm'), primary: true } : null,
      { label: 'Close', onClick: onClose },
    ].filter(Boolean) as SideButton[];
  }

  // Details / Notes switch. Disabled mid-edit so a half-typed change can't be
  // lost by tabbing away.
  const actionStrip: ActionStripTab[] = [
    {
      label: 'Details',
      enabled: mode === 'view',
      active: panel === 'details',
      onClick: () => setPanel('details'),
    },
    {
      label: 'Notes',
      enabled: mode === 'view',
      active: panel === 'notes',
      onClick: () => setPanel('notes'),
    },
  ];

  return (
    <OperaWindow
      title="INDIVIDUAL"
      subtitle={data.workingName}
      status={data.isArchived ? 'ARCHIVED' : 'ACTIVE'}
      recordId={data.id.slice(-8)}
      onClose={onClose}
      size="large"
      sideButtons={sideButtons}
      actionStrip={actionStrip}
    >
      {error && <div className="modal-error">{error}</div>}

      {mode === 'unarchive-confirm' && (
        <OperaSection title="Restore Profile">
          <p style={{ margin: '4px 0 10px', fontSize: 12 }}>
            Restore <strong>{data.workingName}</strong>?<br /><br />
            She will appear in searches again. The archive record is kept — when
            she was archived and why stays on the profile.
          </p>
          {data.archivedAt && (
            <p style={{ margin: '0 0 10px', fontSize: 11, color: 'var(--text-secondary)' }}>
              Archived {fmtClock(data.archivedAt)}
              {data.archivedBy ? ` by ${data.archivedBy}` : ''}
              {data.archivedReason ? ` — "${data.archivedReason}"` : ''}
            </p>
          )}
          <OperaField label="Reason">
            <input
              type="text"
              value={unarchiveReason}
              onChange={e => setUnarchiveReason(e.target.value)}
              placeholder="required — e.g. archived by mistake, back working"
              autoFocus
            />
          </OperaField>
        </OperaSection>
      )}

      {mode === 'archive-confirm' && (
        <OperaSection title="Archive Profile">
          <p style={{ margin: '4px 0 10px', fontSize: 12 }}>
            Archive <strong>{data.workingName}</strong>?<br /><br />
            The profile will be hidden from default searches but kept for history.
            Active stays must be checked-out or cancelled first.
          </p>
          <OperaField label="Reason">
            <input
              type="text"
              value={archiveReason}
              onChange={e => setArchiveReason(e.target.value)}
              placeholder="required — e.g. left town, no longer working"
              autoFocus
            />
          </OperaField>
        </OperaSection>
      )}

      {mode === 'grant-portal-confirm' && (
        <OperaSection title={data.hasPortalAccess ? 'Reset Portal Password' : 'Grant Portal Access'}>
          <p style={{ margin: '4px 0 10px', fontSize: 12 }}>
            {data.hasPortalAccess ? (
              <>
                Reset portal password for <strong>{data.workingName}</strong>?<br /><br />
                A new temporary password will be generated and all her existing
                portal sessions will be revoked. She will need to log in again
                with the new password.
              </>
            ) : (
              <>
                Grant portal access for <strong>{data.workingName}</strong>?<br /><br />
                Her email <strong>{data.email}</strong> will become her username,
                and a temporary password will be generated for her to use on
                first login. She will be assigned to your current property.
              </>
            )}
          </p>
          {!data.email && (
            <div className="modal-error">
              This profile has no email. Edit the profile and add an email before granting portal access.
            </div>
          )}
          {!data.phone && (
            <div className="modal-error">
              This profile has no phone number. Edit the profile and add a phone before granting portal access.
            </div>
          )}
        </OperaSection>
      )}

      {mode === 'portal-result' && tempPassword && (
        <OperaSection title={isReset ? 'Portal Password Reset' : 'Portal Access Granted'}>
          <p style={{ margin: '4px 0 14px', fontSize: 12 }}>
            {isReset ? (
              <>Share this NEW password with <strong>{data.workingName}</strong>. Her previous sessions have been revoked.</>
            ) : (
              <>Share these credentials with <strong>{data.workingName}</strong>. She will be prompted to change the password on first login.</>
            )}
          </p>

          <OperaField label="Username (email)">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontFamily: 'monospace', fontSize: 13, flex: 1 }}>{data.username}</span>
              <button
                type="button"
                onClick={() => copyToClipboard(data.username || '', 'username')}
                style={{ fontSize: 11, padding: '2px 8px' }}
              >
                {copiedField === 'username' ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </OperaField>

          <OperaField label="Temporary password">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 600, letterSpacing: 1, flex: 1 }}>{tempPassword}</span>
              <button
                type="button"
                onClick={() => copyToClipboard(tempPassword, 'password')}
                style={{ fontSize: 11, padding: '2px 8px' }}
              >
                {copiedField === 'password' ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </OperaField>

          <p style={{ margin: '10px 0 0', fontSize: 11, color: 'var(--text-secondary)' }}>
            This password is shown only once. If lost, use Reset Portal to generate a new one.
          </p>
        </OperaSection>
      )}

      {mode === 'add-alias' && (
        <OperaSection title="Add Alias">
          <OperaField label="Alias">
            <input
              type="text"
              value={newAlias}
              onChange={e => setNewAlias(e.target.value)}
              placeholder="e.g. Crimson, Lola, J"
              autoFocus
            />
          </OperaField>
          <OperaField label="Notes">
            <input
              type="text"
              value={newAliasNotes}
              onChange={e => setNewAliasNotes(e.target.value)}
              placeholder="optional — when/where used"
            />
          </OperaField>
        </OperaSection>
      )}

      {panel === 'notes' && mode === 'view' && (
        <ProfileNotesTab
          profileId={data.id}
          workingName={data.workingName}
          onError={setError}
        />
      )}

      {panel === 'details' && (mode === 'view' || mode === 'edit') && (
        <>
          <div className="profile-grid">
            <OperaSection title="Identity">
              <OperaField label="Working name">
                {mode === 'edit'
                  ? <input type="text" value={workingName} onChange={e => setWorkingName(e.target.value)} autoFocus />
                  : <span className="opera-readonly-strong">{data.workingName}</span>}
              </OperaField>
              <OperaField label="Phone">
                {mode === 'edit'
                  ? <input type="text" value={phone} onChange={e => setPhone(e.target.value)} placeholder="0400 000 000" />
                  : <span>{data.phone || '—'}</span>}
              </OperaField>
              <OperaField label="Email">
                {mode === 'edit'
                  ? <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="optional" />
                  : <span>{data.email || '—'}</span>}
              </OperaField>
              <OperaField label="Date of birth">
                {mode === 'edit'
                  ? <input type="date" value={dob} onChange={e => setDob(e.target.value)} />
                  : <span>{data.dateOfBirth ? fmtDate(data.dateOfBirth) : '—'}</span>}
              </OperaField>
            </OperaSection>

            <OperaSection title="Aliases">
              {data.aliases.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>None recorded.</div>}
              {data.aliases.map(a => (
                <div key={a.id} className="profile-alias-row">
                  <span><strong>{a.alias}</strong>{a.notes ? <span style={{ color: 'var(--text-secondary)', marginLeft: 6 }}>— {a.notes}</span> : null}</span>
                  {mode === 'view' && !data.isArchived && (
                    <button type="button" className="alias-remove" onClick={() => handleRemoveAlias(a.id)} disabled={busy}>×</button>
                  )}
                </div>
              ))}
            </OperaSection>

            <OperaSection title="History">
              <OperaField label="Stays">
                <span className="opera-readonly-strong">{data.stats.totalStays}</span>
              </OperaField>
              <OperaField label="Total nights">
                <span>{data.stats.totalNights}</span>
              </OperaField>
              <OperaField label="Last visit">
                <span>{data.stats.lastVisit ? fmtDate(data.stats.lastVisit) : '—'}</span>
              </OperaField>
              <OperaField label="Last room">
                <span>{data.stats.lastRoom || '—'}</span>
              </OperaField>
              <OperaField label="Last rate">
                <span>{data.stats.lastRate !== null ? `$${data.stats.lastRate.toFixed(2)}/night` : '—'}</span>
              </OperaField>
            </OperaSection>
          </div>

          <OperaSection title="Notes">
            {mode === 'edit'
              ? <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Private notes about this profile"
                  style={{ width: '100%', fontSize: 12, padding: 4 }}
                />
              : <div style={{ fontSize: 12, whiteSpace: 'pre-wrap', minHeight: 18 }}>{data.notes || <span style={{ color: 'var(--text-secondary)' }}>—</span>}</div>}
          </OperaSection>

          <OperaSection title="Stay History">
            {data.stays.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>No stays yet.</div>}
            {data.stays.length > 0 && (
              <table className="profile-stays">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Arrival</th>
                    <th>Departure</th>
                    <th>Room</th>
                    <th>Bed</th>
                    <th>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.stays.map(s => (
                    <tr key={s.id}>
                      <td>{statusLabel(s.status)}</td>
                      <td>{fmtDate(s.arrivalDate)}</td>
                      <td>{fmtDate(s.departureDate)}</td>
                      <td>{s.roomDisplayName ? `${s.roomNumber} (${s.roomDisplayName})` : s.roomNumber}</td>
                      <td>{s.bedLabel}</td>
                      <td>${s.nightlyRate.toFixed(2)}/{s.rateType === 'WEEKLY' ? 'wk' : 'nt'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </OperaSection>

          {data.isArchived && data.archivedAt && (
            <div className="opera-audit" style={{ color: 'var(--accent-red)' }}>
              Archived {fmtClock(data.archivedAt)}: {data.archivedReason}
            </div>
          )}

          <div className="opera-audit">
            Created {fmtClock(data.createdAt)}
            {data.updatedAt !== data.createdAt && ` · Updated ${fmtClock(data.updatedAt)}`}
          </div>
        </>
      )}
    </OperaWindow>
  );
}
